import {
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface AppSelectOption {
  value: string | number;
  label: string;
}

@Component({
  selector: 'app-custom-select',
  templateUrl: './custom-select.component.html',
  styleUrls: ['./custom-select.component.css'],
  standalone: true,
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomSelectComponent),
      multi: true
    }
  ]
})
export class CustomSelectComponent implements ControlValueAccessor {
  @Input() options: AppSelectOption[] = [];
  /** Opción vacía / placeholder (ej. "Selecciona tipo"). Si null, no se muestra. */
  @Input() placeholder: string | null = null;
  @Input() placeholderValue: string | number = '';
  @Input() invalid = false;
  @Input() inputId = '';
  @Input() ariaLabelledby = '';
  /** `auth`: más alto, sin borde propio (va dentro de `.auth-input-wrap`). */
  @Input() appearance: 'default' | 'auth' = 'default';

  value: string | number | null = null;
  isOpen = false;
  isDisabled = false;

  private onChange: (value: string | number | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private host: ElementRef<HTMLElement>) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) {
      return;
    }
    const target = event.target as Node | null;
    if (target && !this.host.nativeElement.contains(target)) {
      this.isOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.isOpen = false;
  }

  get selectedLabel(): string {
    if (this.value == null || this.value === '' || this.value === this.placeholderValue) {
      return this.placeholder || 'Selecciona…';
    }
    const found = this.options.find((o) => String(o.value) === String(this.value));
    return found?.label ?? String(this.value);
  }

  get showPlaceholderState(): boolean {
    return (
      this.value == null ||
      this.value === '' ||
      String(this.value) === String(this.placeholderValue)
    );
  }

  writeValue(value: string | number | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  toggle(): void {
    if (this.isDisabled) {
      return;
    }
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.onTouched();
    }
  }

  selectOption(option: AppSelectOption): void {
    if (this.isDisabled) {
      return;
    }
    this.value = option.value;
    this.onChange(option.value);
    this.onTouched();
    this.isOpen = false;
  }

  selectPlaceholder(): void {
    if (this.isDisabled || this.placeholder == null) {
      return;
    }
    this.value = this.placeholderValue;
    this.onChange(this.placeholderValue);
    this.onTouched();
    this.isOpen = false;
  }

  isSelected(option: AppSelectOption): boolean {
    return String(this.value) === String(option.value);
  }
}
